import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import usePaymentConfig from 'state/paymentConfig/hooks/usePaymentConfig';
import {
  TFI_PROCESSOR_ID,
  TFI_PROCESSOR_NAME,
  TFI_ASYNC_MODE_CODE,
} from 'constants/settings/internalConfig';

// Mapping between feature flag keys and their configuration codes
const FEATURE_FLAG_CONFIG_MAP = {
  enableAsyncMode: TFI_ASYNC_MODE_CODE,
  // Add more mappings here for future feature flags
  // enableSystemConfiguration: 'systemConfigCode',
} as const;

interface FeatureFlagsState {
  enableSystemConfiguration: boolean;
  enableAsyncMode: boolean;
}

export const useFeatureFlags = () => {
  const {
    paymentConfig,
    updateGlobalCardReaderValues,
    saveGlobalCardReaderValues,
    toFetchCardReaderValues,
    getCardReaderConfig,
    resetSaveCardReader,
  } = usePaymentConfig();

  const [featureFlags, setFeatureFlags] = useState<FeatureFlagsState>({
    enableSystemConfiguration: false,
    enableAsyncMode: false,
  });

  // Tracks the flag whose save is in flight so its toggle can be disabled —
  // prevents rapid repeat clicks from firing multiple saves/error toasts.
  const [savingFlag, setSavingFlag] = useState<string | null>(null);

  // Fetch initial data
  useEffect(() => {
    toFetchCardReaderValues(TFI_PROCESSOR_ID);
    getCardReaderConfig(TFI_PROCESSOR_NAME);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Populate feature flags from configuration
  useEffect(() => {
    if (!paymentConfig.getCardReaderValues.items) return;

    const updatedFlags: Partial<FeatureFlagsState> = {};

    // Iterate through all feature flag mappings
    Object.entries(FEATURE_FLAG_CONFIG_MAP).forEach(([flagKey, configCode]) => {
      // First, check if there's a DB value for TFI processor
      const dbConfig = paymentConfig.getCardReaderValues.items.find(
        (item) => item.code === configCode && item.processorId === TFI_PROCESSOR_ID
      );

      if (dbConfig) {
        // Use DB value if it exists
        updatedFlags[flagKey as keyof FeatureFlagsState] = dbConfig.value === 'true';
      } else {
        // Otherwise, check for defaultValue in processor schema
        const schemaConfig = paymentConfig.paymentConfiguration?.configurations?.find(
          (item) => item.code === configCode
        );

        if (schemaConfig?.defaultValue) {
          // Use defaultValue from YAML
          updatedFlags[flagKey as keyof FeatureFlagsState] =
            schemaConfig.defaultValue === 'true';
        }
      }
    });

    // Update state only if we found any configurations
    if (Object.keys(updatedFlags).length > 0) {
      setFeatureFlags((prevFlags) => ({
        ...prevFlags,
        ...updatedFlags,
      }));
    }
  }, [
    paymentConfig.getCardReaderValues.items,
    paymentConfig.paymentConfiguration?.configurations,
  ]);

  const handleToggleChange = async (flagName: string, value: boolean) => {
    // Get the configuration code for this flag
    const configCode =
      FEATURE_FLAG_CONFIG_MAP[flagName as keyof typeof FEATURE_FLAG_CONFIG_MAP];
    if (!configCode) {
      return;
    }

    // Ignore clicks while a save is already in flight.
    if (savingFlag) {
      return;
    }

    setSavingFlag(flagName);
    try {
      // Find existing configuration
      const existingConfig = paymentConfig.getCardReaderValues.items?.find(
        (config) => config.code === configCode && config.processorId === TFI_PROCESSOR_ID
      );

      if (!existingConfig) {
        // Create new configuration
        await saveGlobalCardReaderValues({
          name: `TFI_${configCode}`,
          category: null,
          code: configCode,
          value: value.toString(),
          isSecret: false,
          isRequired: false,
          id: uuidv4(),
          processorId: TFI_PROCESSOR_ID,
        });
      } else {
        // Update existing configuration
        await updateGlobalCardReaderValues({
          name: existingConfig.name,
          category: existingConfig.category,
          code: existingConfig.code,
          value: value.toString(),
          isSecret: existingConfig.isSecret,
          isRequired: existingConfig.isRequired,
          id: existingConfig.id,
          processorId: existingConfig.processorId,
        });
      }

      // Wait a bit for the database to be consistent
      // TODO: Replace with proper polling mechanism
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Refetch card reader values to get updated list
      await toFetchCardReaderValues(TFI_PROCESSOR_ID);

      // Clear the shared saveCardReader flag so the Payment Configuration
      // page doesn't pick it up and show its own "Configuration saved" banner.
      resetSaveCardReader();

      // Update local state after successful save
      setFeatureFlags({
        ...featureFlags,
        [flagName]: value,
      });
    } catch (error) {
      console.error('Failed to save configuration:', error);
      toast.error('Failed to save configuration. Please try again.');
    } finally {
      setSavingFlag(null);
    }
  };

  return {
    featureFlags,
    handleToggleChange,
    savingFlag,
  };
};
